import React, { useState, useEffect } from 'react';
import { ArrowRight, Award, Book, BookOpen, Calendar, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import ProgressBar from '@/components/ui/ProgressBar';
import StreakCounter from '@/components/ui/StreakCounter';
import Navigation from '@/components/layout/Navigation';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTotalXP } from '@/hooks/useTotalXP';
import AnimatedXP from '@/components/ui/AnimatedXP';
import { DailyQuestList } from '@/components/quests/DailyQuestList';

const Index = () => {
  const { user } = useAuth();
  const { totalXP, isLoading } = useTotalXP();
  const [streak, setStreak] = useState(5);
  const [dailyGoal, setDailyGoal] = useState(15);
  const [dailyProgress, setDailyProgress] = useState(7);
  
  useEffect(() => {
    if (user?.profile) {
      setStreak(user.profile.streak || streak);
    }
  }, [user]);
  
  // Recent words section replaced with Daily Quests
  
  const achievements = [
    { title: '5 Day Streak', progress: 100, icon: <Calendar className="h-5 w-5 text-orange-500" /> },
    { title: 'Vocabulary Builder', progress: 45, icon: <Book className="h-5 w-5 text-primary" /> },
    { title: 'Perfect Practice', progress: 30, icon: <CheckCircle className="h-5 w-5 text-accent" /> },
  ];
  
  return (
    <div className="min-h-screen pb-20 md:pb-0">
      <div className="container max-w-md mx-auto px-4 py-6">
        {/* Header */}
        <header className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-xl font-bold">Bonjour!</h1>
            <p className="text-sm text-muted-foreground">Ready to practice?</p>
          </div>
          <div className="flex items-center gap-2">
            <AnimatedXP value={totalXP} isLoading={isLoading} />
          </div>
        </header>
        
        {/* Daily Progress */}
        <section className="mb-8 animate-fade-in">
          <StreakCounter count={streak} className="mb-4" />
          
          <Card className="glass-card hover-scale">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                Daily Goal
                <span className="text-xs px-2 py-0.5 rounded-full bg-secondary/20 text-secondary">
                  {dailyProgress}/{dailyGoal} exercises
                </span>
              </CardTitle>
              <CardDescription>Complete your daily goal to maintain your streak</CardDescription>
            </CardHeader>
            <CardContent>
              <ProgressBar 
                value={dailyProgress} 
                max={dailyGoal} 
                color="secondary"
                size="md"
                className="mb-4"
              />
              
              <Button asChild className="w-full">
                <Link to="/learn">
                  Continue Learning
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </section>
        
        {/* Daily Quests */}
        <section className="mb-8 animate-fade-in" style={{ animationDelay: '100ms' }}>
          <DailyQuestList />
        </section>
        
        {/* Achievements */}
        <section className="mb-8 animate-fade-in" style={{ animationDelay: '200ms' }}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Achievements</h2>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/profile">View All</Link>
            </Button>
          </div>
          
          <div className="grid grid-cols-1 gap-3">
            {achievements.map((achievement, index) => (
              <div 
                key={index} 
                className="flex items-center gap-3 p-3 rounded-lg bg-card border border-border animate-fade-in"
                style={{ animationDelay: `${250 + index * 50}ms` }}
              >
                <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                  {achievement.icon}
                </div>
                <div className="flex-1">
                  <p className="font-medium">{achievement.title}</p>
                  <ProgressBar 
                    value={achievement.progress} 
                    max={100} 
                    size="sm"
                  />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
      
      <Navigation />
    </div>
  );
};

export default Index;
